'use client';

import { useEffect, useState } from 'react';
import { FeatherEye, FeatherFileText, FeatherRefreshCw, FeatherSearch, FeatherX } from '@subframe/core';
import { buscarDocumentosPadraoParaArbitragem, preVisualizarDocumentoPadraoParaArbitragem, verificarDocumentoPadraoParaArbitragem } from '@/features/documentosPadrao/store';
import type { DocumentoPadrao, DocumentoPadraoProcessoPickerProps } from '@/features/documentosPadrao/types/documentosPadraoTypes';
import { DOCUMENTO_PADRAO_TIPO_LABEL, podeSerSugerido, situacaoValidade } from '@/features/documentosPadrao/utils/documentosPadraoRegras';
import { Button, Drawer, Loader } from '@/shared/components/ui';
import { isRecord } from '@/shared/utils/apiResponse';
import { formatFileSize } from '@/shared/formatters';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { useAppDispatch } from '@/store/hooks';
const mensagemDeErro = (erro: unknown): string => {
  if (typeof erro === 'string') {
    return /^(failed to fetch|fetch failed|network error)$/i.test(erro) ? 'Não foi possível conectar à biblioteca. Confira sua conexão e tente novamente.' : erro;
  }
  if (erro && typeof erro === 'object' && 'mensagem' in erro && typeof erro.mensagem === 'string') {
    return mensagemDeErro(erro.mensagem);
  }
  return 'Não foi possível concluir a operação. Tente novamente.';
};

/** `FalhaAoUsarDocumentoPadrao.listaDesatualizada`: a recusa diz que a lista mudou no servidor. */
const listaFicouDesatualizada = (falha: unknown): boolean => isRecord(falha) && falha.listaDesatualizada === true;
export function DocumentoPadraoProcessoPicker({
  aberto,
  onFechar,
  nomesNoProcesso,
  destinoLabel,
  processoEncerrado = false,
  onConfirmar
}: DocumentoPadraoProcessoPickerProps) {
  const dispatch = useAppDispatch();
  const [busca, setBusca] = useState('');
  const buscaDebounced = useDebouncedValue(busca.trim(), 350);
  const [pagina, setPagina] = useState(1);
  const [tentativa, setTentativa] = useState(0);
  const [itens, setItens] = useState<DocumentoPadrao[]>([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [referencia, setReferencia] = useState('');
  const [diasAviso, setDiasAviso] = useState(30);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [semPermissao, setSemPermissao] = useState(false);
  const [selecionadoId, setSelecionadoId] = useState<string | null>(null);
  const [preVisualizandoId, setPreVisualizandoId] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [cienteDuplicidade, setCienteDuplicidade] = useState(false);
  const [erroConfirmacao, setErroConfirmacao] = useState<string | null>(null);
  useEffect(() => {
    if (!aberto) return;
    let ativo = true;
    setCarregando(true);
    setErro(null);
    setSemPermissao(false);
    dispatch(buscarDocumentosPadraoParaArbitragem({
      busca: buscaDebounced,
      page: pagina
    })).unwrap().then(dados => {
      if (!ativo) return;
      setItens(atuais => {
        if (pagina === 1) return dados.itens;
        const ids = new Set(atuais.map(item => item.id));
        return [...atuais, ...dados.itens.filter(item_0 => !ids.has(item_0.id))];
      });
      setTotalPaginas(dados.paginacao.totalPages);
      setReferencia(dados.referencia);
      setDiasAviso(dados.diasAvisoVencimento);
    }).catch((falha: unknown) => {
      if (!ativo) return;
      setErro(mensagemDeErro(falha));
      setSemPermissao(Boolean(falha && typeof falha === 'object' && 'status' in falha && (falha.status === 401 || falha.status === 403)));
    }).finally(() => {
      if (ativo) setCarregando(false);
    });
    return () => {
      ativo = false;
    };
  }, [aberto, buscaDebounced, dispatch, pagina, tentativa]);
  const selecionado = itens.find(item_1 => item_1.id === selecionadoId) ?? null;
  const buscaPendente = busca.trim() !== buscaDebounced;
  const duplicado = Boolean(selecionado && nomesNoProcesso.some(nome => nome.toLocaleLowerCase('pt-BR') === selecionado.arquivo.nomeArquivo.toLocaleLowerCase('pt-BR')));
  const selecionadoElegivel = Boolean(selecionado && referencia && podeSerSugerido(selecionado, referencia) && selecionado.produtos.includes('ARBITRAGEM'));
  const fechar = () => {
    if (confirmando) return;
    setBusca('');
    setPagina(1);
    setItens([]);
    setSelecionadoId(null);
    setErroConfirmacao(null);
    setCienteDuplicidade(false);
    onFechar();
  };
  const mudarBusca = (valor: string) => {
    setBusca(valor);
    setPagina(1);
    setSelecionadoId(null);
    setErroConfirmacao(null);
    setCienteDuplicidade(false);
  };
  const preVisualizar = async (id: string) => {
    setPreVisualizandoId(id);
    setErroConfirmacao(null);
    try {
      await dispatch(preVisualizarDocumentoPadraoParaArbitragem(id)).unwrap();
    } catch (falha_0) {
      setErroConfirmacao(mensagemDeErro(falha_0));
    } finally {
      setPreVisualizandoId(null);
    }
  };

  // Da página 1, e não da corrente: o merge por id da paginação manteria a cópia velha de um item
  // de página posterior, e a nova seleção cairia no mesmo descompasso de versão para sempre.
  const recarregarDoInicio = () => {
    setSelecionadoId(null);
    setCienteDuplicidade(false);
    setPagina(1);
    setTentativa(valor_0 => valor_0 + 1);
  };
  const confirmar = async () => {
    if (!selecionado || !selecionadoElegivel || !onConfirmar || duplicado && !cienteDuplicidade) return;
    setConfirmando(true);
    setErroConfirmacao(null);
    try {
      const atual = await dispatch(verificarDocumentoPadraoParaArbitragem(selecionado.id)).unwrap();
      if (!podeSerSugerido(atual, referencia) || !atual.produtos.includes('ARBITRAGEM') || atual.versaoAtual !== selecionado.versaoAtual) {
        setErroConfirmacao('Este documento mudou ou deixou de estar disponível. Atualize a lista e selecione novamente.');
        recarregarDoInicio();
        return;
      }
      await onConfirmar(atual);
      onFechar();
    } catch (falha_1) {
      setErroConfirmacao(mensagemDeErro(falha_1));
      if (listaFicouDesatualizada(falha_1)) recarregarDoInicio();
    } finally {
      setConfirmando(false);
    }
  };
  return <Drawer open={aberto} onOpenChange={open => {
    if (!open) fechar();
  }}>
      <Drawer.Content className="w-full max-w-2xl">
        <div className="flex h-full w-full flex-col">
          <div className="flex items-start justify-between gap-4 border-b border-neutral-100 px-5 py-4 sm:px-6">
            <div>
              <h2 className="text-heading-3 font-heading-3 text-default-font">
                Buscar documento padrão
              </h2>
              <p className="text-body text-subtext-color mt-1">
                Encontre um documento da sua biblioteca para usar neste processo.
              </p>
              {destinoLabel && <p className="text-caption text-subtext-color mt-2">Destino: {destinoLabel}</p>}
            </div>
            <Button variant="neutral-tertiary" size="small" icon={<FeatherX />} onClick={fechar} aria-label="Fechar busca" disabled={confirmando} />
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            <label htmlFor="busca-documento-padrao-processo" className="text-body-bold text-default-font mb-2 block">
              Buscar na biblioteca
            </label>
            <div className="relative">
              <FeatherSearch className="text-subtext-color absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <input id="busca-documento-padrao-processo" autoFocus type="search" value={busca} onChange={evento => mudarBusca(evento.target.value)} placeholder="Nome, tipo ou parte vinculada" className="text-default-font focus-visible:ring-brand-600 bg-default-background h-10 w-full rounded-[8px] border border-neutral-300 pr-3 pl-10 outline-none focus-visible:ring-2" />
            </div>
            <p className="text-caption text-subtext-color mt-2">
              Aparecem somente documentos de Arbitragem que você pode consultar.
            </p>

            <div className="mt-5" aria-live="polite">
              {buscaPendente || carregando && pagina === 1 ? <div className="text-subtext-color flex min-h-40 items-center justify-center gap-2">
                  <Loader size="small" /> Buscando documentos...
                </div> : erro ? <div role="alert" className="rounded-[16px] border border-neutral-200 p-5">
                  <p className="text-default-font font-medium">
                    {semPermissao ? 'Biblioteca indisponível para este acesso' : 'Não foi possível carregar a biblioteca'}
                  </p>
                  <p className="text-subtext-color mt-1 text-sm">{erro}</p>
                  {!semPermissao && <Button className="mt-3" size="small" variant="neutral-secondary" icon={<FeatherRefreshCw />} onClick={() => setTentativa(valor_1 => valor_1 + 1)}>
                      Tentar novamente
                    </Button>}
                </div> : itens.length === 0 ? <div className="rounded-[16px] border border-neutral-200 p-6 text-center">
                  <FeatherFileText className="text-subtext-color mx-auto h-7 w-7" />
                  <p className="text-default-font mt-2 font-medium">
                    {buscaDebounced ? 'Nenhum resultado para esta busca' : 'Nenhum documento padrão disponível'}
                  </p>
                  <p className="text-subtext-color mt-1 text-sm">
                    {buscaDebounced ? 'Tente outro nome ou limpe a busca.' : 'Documentos cadastrados para Arbitragem aparecerão aqui quando estiverem acessíveis para você.'}
                  </p>
                  {buscaDebounced && <Button className="mt-3" variant="neutral-secondary" size="small" onClick={() => mudarBusca('')}>
                      Limpar busca
                    </Button>}
                </div> : <div className="flex flex-col gap-2">
                  {itens.map(item_2 => {
                const situacao = situacaoValidade(item_2.validoAte, referencia, diasAviso);
                const elegivel = podeSerSugerido(item_2, referencia) && item_2.produtos.includes('ARBITRAGEM');
                const selecionado_0 = item_2.id === selecionadoId;
                return <div key={item_2.id} className={`rounded-[16px] border p-3 ${selecionado_0 ? 'border-brand-500 bg-brand-50' : 'bg-default-background border-neutral-200'}`}>
                        <div className="flex items-start gap-3">
                          <button type="button" aria-pressed={selecionado_0} aria-label={`Selecionar ${item_2.nome}`} disabled={!elegivel || confirmando} onClick={() => {
                      setSelecionadoId(item_2.id);
                      setCienteDuplicidade(false);
                      setErroConfirmacao(null);
                    }} className="focus-visible:ring-brand-600 flex min-w-0 flex-1 items-start gap-3 rounded-[8px] text-left outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60">
                            <span aria-hidden="true" className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${selecionado_0 ? 'border-brand-600 bg-brand-600' : 'border-neutral-400'}`}>
                              {selecionado_0 && <span className="bg-default-background h-1.5 w-1.5 rounded-full" />}
                            </span>
                            <span className="flex min-w-0 flex-col gap-1">
                              <span className="text-default-font truncate text-sm font-semibold">
                                {item_2.nome}
                              </span>
                              <span className="text-subtext-color text-xs">
                                {DOCUMENTO_PADRAO_TIPO_LABEL[item_2.tipo]} ·{' '}
                                {item_2.arquivo.nomeArquivo} ·{' '}
                                {formatFileSize(item_2.arquivo.tamanhoBytes)}
                              </span>
                              {item_2.parteVinculada && <span className="text-subtext-color text-xs">
                                  Vinculado a {item_2.parteVinculada.nome}
                                </span>}
                              {!elegivel && <span className="text-error-700 text-xs">
                                  {item_2.arquivado ? 'Arquivado' : situacao === 'VENCIDA' ? 'Validade vencida' : 'Não disponível para Arbitragem'}
                                </span>}
                              {situacao === 'VENCENDO' && <span className="text-warning-700 text-xs">
                                  Validade próxima do fim
                                </span>}
                            </span>
                          </button>
                          <Button variant="neutral-tertiary" size="small" icon={<FeatherEye />} aria-label={`Pré-visualizar ${item_2.nome}`} loading={preVisualizandoId === item_2.id} disabled={preVisualizandoId !== null} onClick={() => preVisualizar(item_2.id)}>
                            Ver
                          </Button>
                        </div>
                      </div>;
              })}
                  {pagina < totalPaginas && <Button className="self-center" variant="neutral-secondary" size="small" loading={carregando} disabled={carregando} onClick={() => setPagina(valor_2 => valor_2 + 1)}>
                      Carregar mais
                    </Button>}
                </div>}
            </div>
          </div>

          <div className="border-t border-neutral-100 px-5 py-4 sm:px-6">
            {selecionado && <div className="mb-3 text-sm">
                <p className="text-default-font font-semibold">Selecionado: {selecionado.nome}</p>
                <p className="text-subtext-color">
                  Versão {selecionado.versaoAtual} · {selecionado.arquivo.nomeArquivo}
                </p>
                {selecionado.parteVinculada && <p className="text-warning-700 mt-1">
                    O vínculo com {selecionado.parteVinculada.nome} precisa ser conferido antes do
                    uso.
                  </p>}
                {duplicado && <label className="text-warning-700 mt-2 flex items-start gap-2">
                    <input type="checkbox" checked={cienteDuplicidade} onChange={evento_0 => setCienteDuplicidade(evento_0.target.checked)} />{' '}
                    Já existe um arquivo com este nome no processo. Quero continuar mesmo assim.
                  </label>}
              </div>}
            {!onConfirmar && processoEncerrado && <p className="text-subtext-color mb-3 text-xs" role="status">
                Este processo está encerrado. Você pode consultar a biblioteca, mas não pode usar um
                documento aqui.
              </p>}
            {erroConfirmacao && <p className="text-error-700 mb-3 text-sm" role="alert">
                {erroConfirmacao}
              </p>}
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="neutral-secondary" onClick={fechar} disabled={confirmando}>
                Cancelar
              </Button>
              <Button onClick={confirmar} loading={confirmando} disabled={!onConfirmar || !selecionadoElegivel || duplicado && !cienteDuplicidade || confirmando}>
                Usar no processo
              </Button>
            </div>
          </div>
        </div>
      </Drawer.Content>
    </Drawer>;
}